import React, { useState, useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import gsap from "gsap";

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const heroRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      heroRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
    );

    gsap.fromTo(
      contentRef.current.querySelectorAll(".animate-section"),
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.2,
        ease: "power3.out",
      }
    );
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setLoading(false);
    }, 1500);
  };

  const handleUnsubscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(false);
      setLoading(false);
      setEmail("");
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <div className="bg-black">
        <Navbar />
      </div>
      
      {/* Hero Section */}
      <div ref={heroRef} className="relative py-20 px-4 text-center text-white bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-6xl mb-6">📧</div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-[#A88006]">
            Newsletter
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Stay updated with the latest fragrance trends, exclusive offers, and new arrivals.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div ref={contentRef} className="max-w-4xl mx-auto px-4 py-16">
        {/* Benefits */}
        <div className="animate-section grid md:grid-cols-3 gap-8 mb-16">
          <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
            <div className="text-3xl mb-4">🎁</div>
            <h3 className="text-lg font-semibold text-black mb-2">Exclusive Offers</h3>
            <p className="text-gray-600 text-sm">Get access to subscriber-only discounts and promotions</p>
          </div>
          <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
            <div className="text-3xl mb-4">🆕</div>
            <h3 className="text-lg font-semibold text-black mb-2">New Arrivals</h3>
            <p className="text-gray-600 text-sm">Be the first to know about new fragrance releases</p>
          </div>
          <div className="text-center bg-white p-6 rounded-xl border border-[#A88006]/20 shadow-lg">
            <div className="text-3xl mb-4">💡</div>
            <h3 className="text-lg font-semibold text-black mb-2">Expert Tips</h3>
            <p className="text-gray-600 text-sm">Learn fragrance application tips and styling advice</p>
          </div>
        </div>

        {/* Subscription Form */}
        <div className="animate-section bg-white p-8 rounded-xl border border-[#A88006]/20 shadow-lg mb-16">
          <h2 className="text-3xl font-bold text-black text-center mb-8">
            {isSubscribed ? "Manage Your Subscription" : "Join Our Newsletter"}
          </h2>
          
          {!isSubscribed ? (
            <form onSubmit={handleSubscribe} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006]"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-[#A88006] to-[#9F7800] text-white font-semibold px-8 py-3 rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
                >
                  {loading ? "Subscribing..." : "Subscribe"}
                </button>
              </div>
              <p className="text-gray-600 text-sm text-center mt-4">
                By subscribing, you agree to receive marketing emails. You can unsubscribe at any time.
              </p>
            </form>
          ) : (
            <div className="max-w-md mx-auto text-center">
              <div className="text-6xl text-green-500 mb-4">✓</div>
              <h3 className="text-xl font-semibold text-black mb-4">Successfully Subscribed!</h3>
              <p className="text-gray-700 mb-6">
                Thank you for subscribing to our newsletter. You'll receive our latest updates and exclusive offers.
              </p>
              <form onSubmit={handleUnsubscribe}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email to unsubscribe"
                  className="w-full px-4 py-3 rounded-lg bg-white border border-gray-300 text-black placeholder-gray-500 focus:outline-none focus:border-[#A88006] mb-4"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full border border-[#A88006] text-[#A88006] font-semibold py-3 rounded-lg hover:bg-[#A88006]/10 transition-all disabled:opacity-50"
                >
                  {loading ? "Unsubscribing..." : "Unsubscribe"}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* What to Expect */}
        <div className="animate-section bg-gradient-to-r from-[#A88006]/10 to-[#9F7800]/10 p-8 rounded-xl border border-[#A88006]/20">
          <h3 className="text-2xl font-bold text-black text-center mb-8">What to Expect</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold text-[#A88006] mb-4">Weekly Newsletter</h4>
              <ul className="space-y-2 text-gray-700">
                <li>• New product launches and restocks</li>
                <li>• Seasonal fragrance recommendations</li>
                <li>• Expert tips and application guides</li>
                <li>• Customer spotlights and reviews</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-[#A88006] mb-4">Exclusive Benefits</h4>
              <ul className="space-y-2 text-gray-700">
                <li>• Early access to sales and promotions</li>
                <li>• Subscriber-only discount codes</li>
                <li>• Free shipping offers</li>
                <li>• Birthday month special offers</li>
              </ul>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-600 text-sm">
              We respect your privacy and will never share your email address. 
              You can unsubscribe at any time with one click.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Newsletter;